import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, 
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useUpdateOtherStandardSchedule, type OtherStandardSchedulePayload } from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

interface Range { start_date: string; end_date: string; price: string }

export interface ScheduleSeed {
  payment_method?: string;
  payment_mode?: string;
  description?: string;
  ranges?: { start_date?: string; end_date?: string; price?: number }[];
}

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const emptyRange = (): Range => ({ start_date: '', end_date: '', price: '' });

/** Edit dialog for the Other.Standard payment schedule (payment mode/method + date/price ranges). */
export function OtherStandardScheduleDialog({
  serviceId,
  open,
  onClose,
  seed,
}: {
  serviceId: number;
  open: boolean;
  onClose: () => void;
  seed: ScheduleSeed;
}) {
  const toast = useToast();
  const update = useUpdateOtherStandardSchedule(serviceId);
  const [method, setMethod] = useState(seed.payment_method || 'prepaid');
  const [mode, setMode] = useState(seed.payment_mode || 'one_time');
  const [ranges, setRanges] = useState<Range[]>(
    seed.ranges?.length
      ? seed.ranges.map((r) => ({ start_date: r.start_date ?? '', end_date: r.end_date ?? '', price: String(r.price ?? '') }))
      : [emptyRange()],
  );

  const setRange = (i: number, key: keyof Range, value: string) =>
    setRanges((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  const addRange = () => { setMode('multiple_times'); setRanges((rs) => [...rs, emptyRange()]); };
  const removeRange = (i: number) => setRanges((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const visibleRanges = mode === 'one_time' ? ranges.slice(0, 1) : ranges;
  const invalid = visibleRanges.some((r) => !r.start_date || !r.end_date || r.price.trim() === '' || Number.isNaN(Number(r.price)));

  const submit = () => {
    if (invalid) return;
    const payload: OtherStandardSchedulePayload = {
      payment_method: method,
      payment_mode: mode,
      description: seed.description || '',
      ranges: visibleRanges.map((r) => ({ start_date: r.start_date, end_date: r.end_date, price: Number(r.price) || 0 })),
    };
    update.mutate(payload, {
      onSuccess: () => { toast.success('Payment schedule updated'); onClose(); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !update.isPending) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Edit payment schedule</DialogTitle>
          <DialogDescription>Set the payment method, mode and one or more date/price ranges.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {update.isError ? <Alert tone="danger" title="Could not save schedule">{errMsg(update.error)}</Alert> : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Payment method">
              {(p) => (
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger id={p.id}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="postpaid">Postpaid</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Payment mode">
              {(p) => (
                <Select value={mode} onValueChange={(v) => { setMode(v); if (v === 'one_time') setRanges((rs) => rs.slice(0, 1)); }}>
                  <SelectTrigger id={p.id}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One time</SelectItem>
                    <SelectItem value="multiple_times">Multiple times</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {visibleRanges.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 'var(--vn-space-2)', alignItems: 'end' }}>
                <Field label={i === 0 ? 'From' : undefined}>
                  {(p) => <DateInput {...p} value={r.start_date} invalid={!r.start_date} onChange={(e) => setRange(i, 'start_date', e.target.value)} />}
                </Field>
                <Field label={i === 0 ? 'To' : undefined}>
                  {(p) => <DateInput {...p} value={r.end_date} invalid={!r.end_date} onChange={(e) => setRange(i, 'end_date', e.target.value)} />}
                </Field>
                <Field label={i === 0 ? 'Price' : undefined}>
                  {(p) => <NumericInput {...p} min={0} value={r.price} invalid={r.price.trim() === ''} onChange={(e) => setRange(i, 'price', e.target.value)} />}
                </Field>
                <Button variant="ghost" size="sm" aria-label="Remove range" disabled={visibleRanges.length <= 1} onClick={() => removeRange(i)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
            {mode === 'multiple_times' ? (
              <Button variant="ghost" size="sm" onClick={addRange} style={{ justifySelf: 'start' }}><Plus size={16} /> Add range</Button>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={update.isPending} onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={update.isPending} disabled={invalid} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
